import type {
  CollectDataTemplateStepUpdateMetadataDto,
  CollectDataTemplateUpdateStepPayloadDto,
} from 'src/interfaces/collectDataTemplateUpdate'
import type { CollectDataTemplateStepRow } from 'src/interfaces/onboardingTemplate'
import {
  metadataForApiStepPayload,
  rowToCreatePayload,
  rowToUpdatePayload,
} from 'src/utils/collectDataTemplateUpdateActions'

function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

const STEP_SCALAR_KEYS = [
  'type',
  'label',
  'order',
  'optional',
  'retroactiveCollectRequired',
  'expiresInDays',
  'requireExpirationDate',
  'maxAttempts',
  'approvalType',
  'ignoreMonitoring',
  'continueOnIncomplete',
  'recollectSameTypeSteps',
  'skipRetroactiveCollectIfAnySameTypeCompleted',
] as const

export function diffStepUpdatePayload(
  prevRow: CollectDataTemplateStepRow,
  nextRow: CollectDataTemplateStepRow,
  serverId: string,
): CollectDataTemplateUpdateStepPayloadDto | null {
  const prevFull = rowToUpdatePayload({ ...prevRow, id: serverId })
  const nextFull = rowToUpdatePayload({ ...nextRow, id: serverId })
  const patch: CollectDataTemplateUpdateStepPayloadDto = { id: serverId }
  let changed = false

  for (const key of STEP_SCALAR_KEYS) {
    const prevVal = prevFull[key]
    const nextVal = nextFull[key]

    if (!jsonEqual(prevVal, nextVal)) {
      Object.assign(patch, { [key]: nextVal })
      changed = true
    }
  }

  const prevMeta = metadataForApiStepPayload(prevRow)
  const nextMeta = metadataForApiStepPayload(nextRow)

  if (!jsonEqual(prevMeta, nextMeta)) {
    if (nextMeta !== undefined) {
      patch.metadata = nextMeta as CollectDataTemplateStepUpdateMetadataDto
    }

    changed = true
  }

  if (!changed) {
    return null
  }

  patch.type = nextFull.type

  return patch
}

export function diffConfigUpdatePayload(
  prev: {
    label: string
    delayed: boolean
    order: number
    title: string | null
    description?: string
  },
  next: {
    label: string
    delayed: boolean
    order: number
    title: string | null
    description?: string
  },
  serverId: string,
): {
  id: string
  label?: string
  delayed?: boolean
  order?: number
  title?: string
  description?: string
} | null {
  const patch: {
    id: string
    label?: string
    delayed?: boolean
    order?: number
    title?: string
    description?: string
  } = { id: serverId }
  let changed = false

  if (prev.label !== next.label) {
    patch.label = next.label
    changed = true
  }

  if (prev.delayed !== next.delayed) {
    patch.delayed = next.delayed
    changed = true
  }

  if (prev.order !== next.order) {
    patch.order = next.order
    changed = true
  }

  const prevTitle = prev.title?.trim() ?? ''
  const nextTitle = next.title?.trim() ?? ''

  if (prevTitle !== nextTitle) {
    patch.title = nextTitle !== '' ? nextTitle : undefined
    changed = true
  }

  const prevDesc = prev.description?.trim() ?? ''
  const nextDesc = next.description?.trim() ?? ''

  if (prevDesc !== nextDesc) {
    patch.description = nextDesc !== '' ? nextDesc : undefined
    changed = true
  }

  if (!changed) {
    return null
  }

  return patch
}

export { rowToCreatePayload }
